import { ApiResponse } from '../types/api';

const LOCK_ERROR_PATTERN = /lock|busy|concurrent|timeout|timed out|retry/i;

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  sleep?: (ms: number) => Promise<void>;
  shouldRetry?: (error: unknown) => boolean;
}

export class LockTimeoutError extends Error {
  constructor(message = 'Could not acquire lock. Server busy. Please retry.') {
    super(message);
    this.name = 'LockTimeoutError';
  }
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function describe(input: unknown): string {
  if (typeof input === 'string') return input;
  if (input instanceof Error) return input.message;
  if (input && typeof input === 'object') {
    const record = input as Record<string, unknown>;
    const parts = [record.message, record.error, record.errors]
      .map((value) => (Array.isArray(value) ? value.join(' ') : value))
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
    return parts.join(' ');
  }
  return String(input ?? '');
}

export function isLockError(input: unknown): boolean {
  return LOCK_ERROR_PATTERN.test(describe(input));
}

export function backoffDelay(attempt: number, baseDelay = 1000, maxDelay = 30000): number {
  return Math.min(maxDelay, baseDelay * Math.pow(2, attempt));
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    sleep = defaultSleep,
    shouldRetry = () => true,
  } = options;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries - 1 || !shouldRetry(error)) {
        throw error;
      }
      await sleep(backoffDelay(attempt, baseDelay, maxDelay));
    }
  }

  throw new LockTimeoutError('Max retries exceeded');
}

export async function retryIfLocked<T extends ApiResponse<unknown>>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const { maxRetries = 3, baseDelay = 1000, maxDelay = 30000, sleep = defaultSleep } = options;

  let last: T | undefined;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    last = await fn();
    if (last.success !== false || !isLockError(last)) {
      return last;
    }
    if (attempt < maxRetries - 1) {
      await sleep(backoffDelay(attempt, baseDelay, maxDelay));
    }
  }

  return last as T;
}
